import { useEffect, useMemo, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Linking } from 'react-native';
import { Download, ExternalLink, Link, Pencil, Plus } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { Button } from '../common/Button';
import { TextLink } from '../common/TextLink';
import { PlanEventForm } from './PlanEventForm.web';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { usePlanEventsStore } from '../../store/planEventsStore';
import { useUIStore } from '../../store/uiStore';
import { appAlert } from '../../utils/appAlert';
import { downloadText, isDownloadSupported } from '../../utils/downloadFile';
import {
  formatEventWhen,
  isEventUpcoming,
  sortEventsForList,
  upcomingEventsForBundle,
} from '../../utils/eventCalendar';
import {
  buildEventRsvpCsv,
  eventRsvpFilename,
  rsvpEmailList,
  rsvpHeadcount,
} from '../../utils/eventRsvpCsv';
import { planBounds } from '../../utils/planGeometry';
import { planEventShareUrl, sharePlanEvent } from '../../utils/share';
import type { PlanEvent, PlanEventRsvp } from '../../types';
import type { PlanEventInput } from '../../api/planEvents';

type Mode =
  | { kind: 'list' }
  | { kind: 'form'; eventId?: string }
  | { kind: 'detail'; eventId: string };

const NO_EVENTS: PlanEvent[] = [];
const NO_RSVPS: PlanEventRsvp[] = [];

function peopleText(n: number): string {
  return `${n} ${n === 1 ? 'person' : 'people'}`;
}

function EventRow({ event, now, onPress }: { event: PlanEvent; now: Date; onPress: () => void }) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const upcoming = isEventUpcoming(event, now);
  const muted = !upcoming;
  const status = event.status === 'cancelled' ? 'Cancelled' : muted ? 'Ended' : null;
  return (
    <Pressable
      onPress={onPress}
      testID={`plan-event-row-${event.id}`}
      accessibilityRole="button"
      accessibilityLabel={`${event.title}, ${formatEventWhen(event)}`}
      {...hoverProps}
      style={[
        styles.row,
        CURSOR_POINTER,
        {
          borderColor: colors.border,
          backgroundColor: hovered ? colors.hoverSurface : 'transparent',
        },
      ]}>
      <Text
        style={[styles.rowTitle, { color: muted ? colors.textSecondary : colors.text }]}
        numberOfLines={1}>
        {event.title}
      </Text>
      <Text style={[styles.rowMeta, { color: colors.textSecondary }]} numberOfLines={1}>
        {formatEventWhen(event, 'en-US', now)}
      </Text>
      <Text style={[styles.rowMeta, { color: colors.textTertiary }]} numberOfLines={1}>
        {status ? `${status} · ` : ''}
        {event.goingCount} going
      </Text>
    </Pressable>
  );
}

function RsvpRow({ rsvp }: { rsvp: PlanEventRsvp }) {
  const { colors } = useTheme();
  return (
    <View
      style={[styles.rsvpRow, { borderColor: colors.border }]}
      testID={`plan-event-rsvp-${rsvp.id}`}>
      <View style={styles.rsvpText}>
        <Text style={[styles.rowTitle, { color: colors.text }]} numberOfLines={1}>
          {rsvp.name}
        </Text>
        <Text style={[styles.rowMeta, { color: colors.textSecondary }]} numberOfLines={1}>
          {rsvp.email}
        </Text>
      </View>
      <Text style={[styles.rowMeta, { color: colors.textSecondary }]}>
        {peopleText(rsvp.partySize)}
      </Text>
    </View>
  );
}

/**
 * Owner-only: a bundle's events. A list of them, a form to schedule or edit
 * one, and a detail view with the share link, the sign-ups and the cancel and
 * delete actions. Hosted by a CompanionPanel on the desktop shell and by
 * PlanEventsSheet elsewhere, the same split as the plan updates view.
 */
export function PlanEventsPanel({
  planId,
  bundleId,
  hideTitle = false,
}: {
  planId: string;
  bundleId: string;
  /** The desktop companion's header already says "Events" and names the
   *  bundle, so the view's own title would repeat it. */
  hideTitle?: boolean;
}) {
  const { colors } = useTheme();
  const ownPlan = useDefensePlansStore((s) => s.ownPlan);
  const setVisibility = useDefensePlansStore((s) => s.setVisibility);
  const allEvents = usePlanEventsStore((s) => s.eventsByPlan[planId]) ?? NO_EVENTS;
  const rsvpsByEvent = usePlanEventsStore((s) => s.rsvpsByEvent);
  const rsvpsLoaded = usePlanEventsStore((s) => s.rsvpsLoaded);
  const isSaving = usePlanEventsStore((s) => s.isSaving);
  const storeError = usePlanEventsStore((s) => s.error);
  const createEvent = usePlanEventsStore((s) => s.createEvent);
  const updateEvent = usePlanEventsStore((s) => s.updateEvent);
  const cancelEvent = usePlanEventsStore((s) => s.cancelEvent);
  const deleteEvent = usePlanEventsStore((s) => s.deleteEvent);
  const subscribeRsvps = usePlanEventsStore((s) => s.subscribeRsvps);
  const unsubscribeRsvps = usePlanEventsStore((s) => s.unsubscribeRsvps);
  const clearError = usePlanEventsStore((s) => s.clearError);

  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const now = new Date();

  const bundle =
    ownPlan?.id === planId ? ownPlan.bundles.find((b) => b.id === bundleId) : undefined;
  const members = useMemo(
    () => (ownPlan?.id === planId ? ownPlan.features.filter((f) => f.bundleId === bundleId) : []),
    [ownPlan, planId, bundleId]
  );
  const defaultPlace = useMemo(() => {
    const bounds = planBounds(members);
    return bounds ? { lat: (bounds.n + bounds.s) / 2, lng: (bounds.e + bounds.w) / 2 } : null;
  }, [members]);

  const events = useMemo(
    () =>
      sortEventsForList(
        allEvents.filter((e) =>
          e.links.some((l) => l.planId === planId && l.bundleId === bundleId)
        ),
        now
      ),
    // `now` moves every render; a stale label until the next snapshot is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allEvents, planId, bundleId]
  );

  const detailId = mode.kind === 'detail' ? mode.eventId : null;
  const detailEvent = detailId ? events.find((e) => e.id === detailId) : undefined;

  // Sign-ups are owner-only and cost a listener, so they load only while a
  // detail view is open.
  useEffect(() => {
    if (!detailId) return;
    subscribeRsvps(detailId);
    return () => unsubscribeRsvps();
  }, [detailId, subscribeRsvps, unsubscribeRsvps]);

  // An error from a past screen should not greet the next one.
  useEffect(() => {
    clearError();
    return () => clearError();
  }, [mode.kind, clearError]);

  const toast = (message: string) => useUIStore.getState().showToast({ message });
  const goList = () => setMode({ kind: 'list' });

  const handleSubmitForm = async (input: PlanEventInput): Promise<boolean> => {
    if (mode.kind !== 'form') return false;
    if (mode.eventId) {
      const ok = await updateEvent(mode.eventId, input);
      if (ok) {
        toast('Event saved');
        setMode({ kind: 'detail', eventId: mode.eventId });
      }
      return ok;
    }
    const id = await createEvent(planId, bundleId, input);
    if (id) {
      toast('Event scheduled');
      setMode({ kind: 'detail', eventId: id });
      return true;
    }
    return false;
  };

  const handleCopyLink = async (event: PlanEvent) => {
    const result = await sharePlanEvent({ eventId: event.id, title: event.title });
    if (result === 'copied') toast('Link copied');
    else if (result === 'failed') toast("Couldn't copy the link");
  };

  const handleOpenPage = (event: PlanEvent) => {
    void Linking.openURL(planEventShareUrl(event.id));
  };

  const handleCancel = (event: PlanEvent) => {
    appAlert('Cancel this event?', 'People who open the link will see that it was cancelled.', [
      { text: 'Keep event', style: 'cancel' },
      {
        text: 'Cancel event',
        style: 'destructive',
        onPress: () => {
          void cancelEvent(event.id).then((ok) =>
            toast(ok ? 'Event cancelled' : "Couldn't cancel the event")
          );
        },
      },
    ]);
  };

  const handleDelete = (event: PlanEvent) => {
    appAlert('Delete this event?', 'The link stops working and the sign-up list is lost.', [
      { text: 'Keep event', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          void deleteEvent(event.id).then((ok) => {
            if (ok) {
              toast('Event deleted');
              goList();
            } else {
              toast("Couldn't delete the event");
            }
          });
        },
      },
    ]);
  };

  const handleShareTheWork = async () => {
    const saved = await setVisibility('shared');
    toast(saved ? 'Plan shared' : "Couldn't change who can see this plan");
  };

  const handleCopyEmails = async (rsvps: PlanEventRsvp[]) => {
    try {
      await navigator.clipboard.writeText(rsvpEmailList(rsvps));
      toast('Emails copied');
    } catch {
      toast("Couldn't copy the emails");
    }
  };

  const handleCsv = (event: PlanEvent, rsvps: PlanEventRsvp[]) => {
    downloadText(buildEventRsvpCsv(event, rsvps), eventRsvpFilename(event), 'text/csv');
  };

  const upcomingCount = upcomingEventsForBundle(events, planId, bundleId, now).length;

  return (
    <View style={styles.content} testID="plan-events-panel">
      {!hideTitle && (
        <Text style={[styles.title, { color: colors.text }]}>
          {bundle ? `Events for ${bundle.name}` : 'Events'}
        </Text>
      )}

      {mode.kind === 'list' && (
        <>
          <Button
            title="New event"
            onPress={() => setMode({ kind: 'form' })}
            variant="primary"
            size="sm"
            leftIcon={Plus}
            fullWidth
            testID="plan-events-new"
          />
          {events.length === 0 ? (
            <Text style={[styles.empty, { color: colors.textTertiary }]} testID="plan-events-empty">
              No events yet. Pick a time and a place, and neighbors can sign up to help.
            </Text>
          ) : (
            <View style={styles.list}>
              {upcomingCount === 0 && (
                <Text style={[styles.hint, { color: colors.textSecondary }]}>
                  Nothing coming up.
                </Text>
              )}
              {events.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  now={now}
                  onPress={() => setMode({ kind: 'detail', eventId: event.id })}
                />
              ))}
            </View>
          )}
        </>
      )}

      {mode.kind === 'form' && (
        <>
          <TextLink
            label="All events"
            onPress={() =>
              mode.eventId ? setMode({ kind: 'detail', eventId: mode.eventId }) : goList()
            }
            textStyle={TYPOGRAPHY.caption}
            testID="plan-events-back"
          />
          <PlanEventForm
            // A different event (or a fresh one) is a different form.
            key={mode.eventId ?? 'new'}
            bundleName={bundle?.name ?? ''}
            defaultPlace={defaultPlace}
            event={mode.eventId ? events.find((e) => e.id === mode.eventId) : undefined}
            saving={isSaving}
            serverError={storeError}
            onSubmit={handleSubmitForm}
            onCancel={() =>
              mode.eventId ? setMode({ kind: 'detail', eventId: mode.eventId }) : goList()
            }
          />
        </>
      )}

      {mode.kind === 'detail' && (
        <>
          <TextLink
            label="All events"
            onPress={goList}
            textStyle={TYPOGRAPHY.caption}
            testID="plan-events-back"
          />
          {!detailEvent ? (
            <Text style={[styles.empty, { color: colors.textTertiary }]}>
              That event isn&apos;t available.
            </Text>
          ) : (
            <EventDetail
              event={detailEvent}
              now={now}
              sharedPlan={ownPlan?.visibility === 'shared'}
              rsvps={rsvpsByEvent[detailEvent.id] ?? NO_RSVPS}
              rsvpsLoaded={rsvpsLoaded}
              onCopyLink={() => void handleCopyLink(detailEvent)}
              onOpenPage={() => handleOpenPage(detailEvent)}
              onEdit={() => setMode({ kind: 'form', eventId: detailEvent.id })}
              onCancel={() => handleCancel(detailEvent)}
              onDelete={() => handleDelete(detailEvent)}
              onSharePlan={() => void handleShareTheWork()}
              onCopyEmails={(rsvps) => void handleCopyEmails(rsvps)}
              onCsv={(rsvps) => handleCsv(detailEvent, rsvps)}
              error={storeError}
            />
          )}
        </>
      )}

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="plan-events-end" style={styles.scrollSentinel} />
    </View>
  );
}

function EventDetail({
  event,
  now,
  sharedPlan,
  rsvps,
  rsvpsLoaded,
  onCopyLink,
  onOpenPage,
  onEdit,
  onCancel,
  onDelete,
  onSharePlan,
  onCopyEmails,
  onCsv,
  error,
}: {
  event: PlanEvent;
  now: Date;
  sharedPlan: boolean;
  rsvps: PlanEventRsvp[];
  rsvpsLoaded: boolean;
  onCopyLink: () => void;
  onOpenPage: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onSharePlan: () => void;
  onCopyEmails: (rsvps: PlanEventRsvp[]) => void;
  onCsv: (rsvps: PlanEventRsvp[]) => void;
  error: string | null;
}) {
  const { colors } = useTheme();
  const upcoming = isEventUpcoming(event, now);
  const status =
    event.status === 'cancelled'
      ? 'This event was cancelled.'
      : !upcoming
        ? 'This event has ended.'
        : null;
  const headcount = rsvpHeadcount(rsvps);
  return (
    <View style={styles.detail} testID={`plan-event-detail-${event.id}`}>
      <View>
        <Text style={[styles.detailTitle, { color: colors.text }]}>{event.title}</Text>
        <Text style={[styles.detailMeta, { color: colors.textSecondary }]} testID="plan-event-when">
          {formatEventWhen(event, 'en-US', now)}
        </Text>
        <Text style={[styles.detailMeta, { color: colors.textSecondary }]}>
          {event.meetingPlace.label}
        </Text>
        {status && (
          <Text style={[styles.detailMeta, { color: colors.textTertiary }]}>{status}</Text>
        )}
      </View>

      {!sharedPlan && (
        <View testID="plan-events-private-note" style={styles.privateNote}>
          <Text style={[styles.hint, { color: colors.textSecondary }]}>
            This plan is private, so people with the link will see the time and place but not the
            work.
          </Text>
          <TextLink
            label="Share the plan"
            onPress={onSharePlan}
            textStyle={TYPOGRAPHY.caption}
            testID="plan-events-share-plan"
          />
        </View>
      )}

      <View style={styles.actionGrid}>
        <Button
          title="Copy link"
          onPress={onCopyLink}
          variant="secondary"
          size="sm"
          leftIcon={Link}
          style={styles.half}
          testID="plan-event-copy"
        />
        <Button
          title="Open page"
          onPress={onOpenPage}
          variant="secondary"
          size="sm"
          rightIcon={ExternalLink}
          style={styles.half}
          testID="plan-event-open"
        />
      </View>
      <View style={styles.actionGrid}>
        <Button
          title="Edit"
          onPress={onEdit}
          variant="secondary"
          size="sm"
          leftIcon={Pencil}
          style={styles.half}
          testID="plan-event-edit"
        />
        {event.status === 'scheduled' && upcoming ? (
          <Button
            title="Cancel event"
            onPress={onCancel}
            variant="danger"
            size="sm"
            style={styles.half}
            testID="plan-event-cancel"
          />
        ) : (
          <Button
            title="Delete"
            onPress={onDelete}
            variant="danger"
            size="sm"
            style={styles.half}
            testID="plan-event-delete"
          />
        )}
      </View>
      {error ? <Text style={[styles.hint, { color: colors.danger }]}>{error}</Text> : null}

      <View style={styles.rsvpSection} testID="plan-event-rsvps">
        <Text style={[styles.overline, { color: colors.textSecondary }]}>
          {`Signed up${headcount > 0 ? ` · ${peopleText(headcount)}` : ''}`}
        </Text>
        {rsvps.length === 0 ? (
          <Text
            style={[styles.hint, { color: colors.textTertiary }]}
            testID="plan-event-rsvps-empty">
            {rsvpsLoaded ? 'No one has signed up yet.' : ' '}
          </Text>
        ) : (
          <>
            {rsvps.map((rsvp) => (
              <RsvpRow key={rsvp.id} rsvp={rsvp} />
            ))}
            <View style={styles.actionGrid}>
              <Button
                title="Copy emails"
                onPress={() => onCopyEmails(rsvps)}
                variant="secondary"
                size="sm"
                style={styles.half}
                testID="plan-event-copy-emails"
              />
              {isDownloadSupported() && (
                <Button
                  title="Download CSV"
                  onPress={() => onCsv(rsvps)}
                  variant="secondary"
                  size="sm"
                  leftIcon={Download}
                  style={styles.half}
                  testID="plan-event-csv"
                />
              )}
            </View>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  list: {
    gap: SPACING.xs,
  },
  empty: {
    ...TYPOGRAPHY.body,
  },
  hint: {
    ...TYPOGRAPHY.caption,
  },
  row: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    gap: 2,
  },
  rowTitle: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'medium'),
  },
  rowMeta: {
    ...TYPOGRAPHY.caption,
  },
  detail: {
    gap: SPACING.sm,
  },
  detailTitle: {
    ...TYPOGRAPHY.h4,
  },
  detailMeta: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  privateNote: {
    gap: SPACING.xs,
    alignItems: 'flex-start',
  },
  actionGrid: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  half: {
    flex: 1,
  },
  rsvpSection: {
    gap: SPACING.xs,
    marginTop: SPACING.md,
  },
  overline: {
    ...TYPOGRAPHY.overline,
  },
  rsvpRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rsvpText: {
    flex: 1,
    minWidth: 0,
  },
  scrollSentinel: {
    height: 1,
  },
});
